import type { AbstractPartDefinition } from "@project/emerge-types";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import type { ProductViewerRenderQuality } from "./productViewerRenderQuality";

export type ProductViewerLoadingScreen = Exclude<ReactNode, boolean> | false;
export type CameraAutoFitTrigger = "configChange" | "canvasResize";
export type CameraAutoFit = false | readonly CameraAutoFitTrigger[];

export enum CameraControlsMode {
  FixedOrbit = "fixed-orbit",
  FreeOrbit = "free-orbit",
  None = "none",
}

export const DEFAULT_CAMERA_CONTROLS_MODE = CameraControlsMode.FixedOrbit;

/**
 * How a user starts a drag on a part.
 * - `modifier` (default): ⌘/Ctrl must be held, both for the drag itself and for
 *   the drag-mode hover affordances (movement outline, arrow cross, cursor).
 * - `direct`: the part is armed - outlined on the case, selectable by a plain
 *   click - and, once the host has selected it (`isPartSelected`), a plain
 *   left-press drags it. Use it once the user has been put into an explicit
 *   placement mode, e.g. the step that configures the part.
 */
export type DragActivation = "modifier" | "direct";

/** Identifies the part a viewer interaction happened on. */
export interface PartInteractionInfo {
  partId: string;
  partType: string;
  buildstepName: string;
  /** `dragConstraint.sourceOptionUuid`; links back to a `variableOptions` entry. */
  sourceOptionUuid: string;
}

/**
 * Host-controlled part interaction behaviour. Every field is optional and the
 * defaults reproduce the viewer's built-in behaviour, so consumers that do not
 * pass an `interaction` config (e.g. the internal configurator) are unaffected.
 */
export interface ProductViewerInteractionConfig {
  /**
   * Decides whether a part that carries a `dragConstraint` is actually
   * interactive. Returning `false` renders it as a static part: no drag, no
   * hover affordance, no click. Defaults to every constrained part.
   */
  isPartDraggable?: (part: AbstractPartDefinition<string>) => boolean;
  /**
   * Drag activation mode. Either one mode for every draggable part, or a
   * predicate that decides per part - so a host can arm only the parts the user
   * is currently working on and leave the rest on the modifier gate. Defaults
   * to `modifier` everywhere.
   */
  dragActivation?:
    | DragActivation
    | ((part: AbstractPartDefinition<string>) => DragActivation);
  /**
   * Which parts the host is pointing out: a part it returns `true` for draws
   * the full glow through the case, whether or not it is armed, the way a part
   * under the pointer does. This is the channel for a host UI that lists the
   * parts and wants the one under the pointer's row to stand out; its armed
   * siblings keep their outline. Does not change what a press does. Defaults
   * to `false` everywhere.
   */
  isPartPointedOut?: (part: AbstractPartDefinition<string>) => boolean;
  /**
   * Which parts the host has selected - the ones being worked on. A selected
   * part draws its direction arrows, which are also the handles for an
   * axis-locked drag; nothing else does, so a step with a dozen armed parts
   * shows one arrow cross. In `direct` mode it is also the part a plain press
   * drags; a press on any other armed part is a click (`onPartClick`) or an
   * orbit. Defaults to `false` everywhere.
   */
  isPartSelected?: (part: AbstractPartDefinition<string>) => boolean;
  /** Called when a draggable part is clicked (a press that never moved). */
  onPartClick?: (info: PartInteractionInfo) => void;
  /**
   * Called on the press that opens a drag session on a part, before it is
   * known whether the press will travel: a plain click reports here first
   * and then on `onPartClick`. The place to select the part the user grabbed,
   * and not to move the camera - a move mid-drag changes the drag plane.
   */
  onPartDragStart?: (info: PartInteractionInfo) => void;
  /**
   * Called when the pointer enters a draggable part (with the part) and when
   * it leaves it again (with `null`). Fires on the boundary crossing only, not
   * on every move, so a host can mirror the hover in its own UI.
   */
  onPartHover?: (info: PartInteractionInfo | null) => void;
}

export interface ProductViewerConfig {
  loadingScreen?: ProductViewerLoadingScreen;
  cameraControls?: CameraControlsMode;
  cameraAutoFit?: CameraAutoFit;
  interaction?: ProductViewerInteractionConfig;
  /** Motion pixel/shadow budget; every preset restores full quality at rest. */
  renderQuality?: ProductViewerRenderQuality;
  /**
   * Extra content rendered inside the viewer's canvas, beside the product.
   * For tooling that needs the live three.js scene (`useThree`), such as the
   * lt-cases diagnostics; nothing in it belongs to the product. It is part of
   * the context's memo dependencies, so pass a stable (memoised) element.
   */
  sceneExtras?: ReactNode;
}

/** Default when no `ProductViewerConfigProvider` is used (emerge direct consumers). */
const EMERGE_CONTEXT_DEFAULT_CAMERA_CONTROLS = CameraControlsMode.FreeOrbit;

const ProductViewerConfigContext = createContext<ProductViewerConfig>({
  cameraControls: EMERGE_CONTEXT_DEFAULT_CAMERA_CONTROLS,
});

export const ProductViewerConfigProvider = ({
  children,
  loadingScreen,
  cameraControls = DEFAULT_CAMERA_CONTROLS_MODE,
  cameraAutoFit = false,
  interaction,
  renderQuality,
  sceneExtras,
}: ProductViewerConfig & {
  children: ReactNode;
}) => {
  const value = useMemo<ProductViewerConfig>(
    () => ({
      loadingScreen,
      cameraControls,
      cameraAutoFit,
      interaction,
      renderQuality,
      sceneExtras,
    }),
    [
      loadingScreen,
      cameraControls,
      cameraAutoFit,
      interaction,
      renderQuality,
      sceneExtras,
    ],
  );

  return (
    <ProductViewerConfigContext.Provider value={value}>
      {children}
    </ProductViewerConfigContext.Provider>
  );
};

export const useProductViewerConfig = () =>
  useContext(ProductViewerConfigContext);
